import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type DxfImportPhase = 'uploading' | 'fetching' | 'applying' | 'error';
export type DxfImportMode = 'upload' | 'open';

export type DxfImportState = {
  active: boolean;
  mode: DxfImportMode;
  phase: DxfImportPhase;
  fileName: string;
  projectName: string;
  message: string;
  progress: number | null;
  error: string | null;
  generation: number;
};

const initialState: DxfImportState = {
  active: false,
  mode: 'upload',
  phase: 'uploading',
  fileName: '',
  projectName: '',
  message: '',
  progress: null,
  error: null,
  generation: 0,
};

const dxfImportSlice = createSlice({
  name: 'dxfImport',
  initialState,
  reducers: {
    startDxfImport: (
      state,
      action: PayloadAction<{ fileName: string; projectName: string }>
    ) => {
      state.active = true;
      state.mode = 'upload';
      state.phase = 'uploading';
      state.fileName = action.payload.fileName;
      state.projectName = action.payload.projectName;
      state.message = `Uploading “${action.payload.fileName}”…`;
      state.progress = 0;
      state.error = null;
      state.generation += 1;
    },
    /** Open an already-uploaded floor plan — skip upload phase. */
    startDxfOpen: (
      state,
      action: PayloadAction<{ projectName: string; fileName?: string }>
    ) => {
      state.active = true;
      state.mode = 'open';
      state.phase = 'fetching';
      state.fileName = action.payload.fileName ?? '';
      state.projectName = action.payload.projectName;
      state.message = `Opening “${action.payload.projectName}”…`;
      state.progress = 0;
      state.error = null;
      state.generation += 1;
    },
    setDxfImportPhase: (
      state,
      action: PayloadAction<{
        phase: Exclude<DxfImportPhase, 'error'>;
        message: string;
        progress?: number | null;
        forceProgress?: boolean;
      }>
    ) => {
      if (!state.active) return;
      state.phase = action.payload.phase;
      state.message = action.payload.message;
      if (action.payload.progress !== undefined && action.payload.progress !== null) {
        const next = Math.max(0, Math.min(100, Math.round(action.payload.progress)));
        if (action.payload.forceProgress) {
          state.progress = next;
        } else {
          state.progress = state.progress !== null ? Math.max(state.progress, next) : next;
        }
      }
      state.error = null;
    },
    setDxfImportError: (state, action: PayloadAction<string>) => {
      state.active = true;
      state.phase = 'error';
      state.error = action.payload;
      state.message = action.payload;
    },
    /** Clear an interrupt error and resume polling without starting a new upload. */
    resumeDxfImportFromError: (
      state,
      action: PayloadAction<{ phase?: Exclude<DxfImportPhase, 'error'>; message: string }>
    ) => {
      if (!state.projectName) return;
      state.active = true;
      state.phase = action.payload.phase ?? (state.mode === 'open' ? 'fetching' : 'uploading');
      state.message = action.payload.message;
      state.error = null;
      state.generation += 1;
    },
    clearDxfImport: (state) => {
      state.active = false;
      state.mode = 'upload';
      state.phase = 'uploading';
      state.fileName = '';
      state.projectName = '';
      state.message = '';
      state.progress = null;
      state.error = null;
      // Keep generation so in-flight work can detect cancellation.
    },
  },
});

export const {
  startDxfImport,
  startDxfOpen,
  setDxfImportPhase,
  setDxfImportError,
  resumeDxfImportFromError,
  clearDxfImport,
} = dxfImportSlice.actions;

export default dxfImportSlice.reducer;
